/* eslint-disable react/no-unused-state */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import { gettext } from '@eb/i18n';

import debounce from 'lodash/debounce';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';

import { HAS_WINDOW } from 'eventbrite_design_system/utils/dom';
import CrosshairSvg from 'eventbrite_design_system/iconography/icons/Crosshair';
import AutocompleteField from './AutocompleteFieldWithFallback';
import { getPlaceFromGooglePlaceId as getPlaceFromGooglePlaceIdApi } from '../redux/location/api';
import {
    parseHistorySuggestions,
    getOnlineEventsOption,
} from '../utils/suggestions';
import { LOCATION_STORAGE_KEY } from '../constants';

import './LocationAutocomplete.scss';

const isGoogleMapsLoaded = () =>
    HAS_WINDOW && window.google && window.google.maps;

const LOCATION_DEBOUNCE_INTERVAL = 250;
const BBOX_SEARCH_DISPLAY_STRING = gettext('map area');

// When Google Places Autocomplete returns a Place Object, we attempt to match it
// to our internal Who's On First (WOF) database, because the app depends on WOF Place Ids
// and WOF location slugs. In some cases our WOF db does not have a match, and we
// want to track how often it happens.
// This is done in the library instead of the apps, because we want to track this
// for all apps at once.
const trackMissingWOFPlace = (locationName) => {
    if (window.ga) {
        window.ga('send', {
            hitType: 'event',
            eventCategory: 'search',
            eventAction: 'MissingWOFPlace',
            eventLabel: locationName,
            // nonInteraction is so we don't pollute the analytics
            nonInteraction: true,
        });
    }
};

// Takes a clicked-on location from the Autocomplete suggestions prop,
// and calls our own API for our internal location properties
const getPlaceObj = (suggestedLocation) => {
    const googlePlaceId = suggestedLocation.value;

    return new Promise((res) => {
        getPlaceFromGooglePlaceIdApi(googlePlaceId).then((response) => {
            const { place } = response;

            if (place) {
                res({
                    currentPlace: suggestedLocation.currentPlace,
                    currentPlaceParent: suggestedLocation.currentPlaceParent,
                    latitude: place.latitude,
                    longitude: place.longitude,
                    slug: place.location_slug,
                    placeType: place.place_type,
                    placeId: place.id,
                    isOnline: false,
                });
            } else {
                // if the API can't find a corresponding WOF place ID for the google place ID
                // it returns a lat/lng and viewport
                const {
                    latitude,
                    longitude,
                    viewport_northeast,
                    viewport_southwest,
                } = response;

                // only 'sw' and 'ne' are necesary but at least one line of code uses 'se'
                // so we include 'nw' and 'se' just to be safe.
                const bbox = {
                    nw: {
                        lng: viewport_southwest.longitude,
                        lat: viewport_northeast.latitude,
                    },
                    sw: {
                        lng: viewport_southwest.longitude,
                        lat: viewport_southwest.latitude,
                    },
                    ne: {
                        lng: viewport_northeast.longitude,
                        lat: viewport_northeast.latitude,
                    },
                    se: {
                        lng: viewport_northeast.longitude,
                        lat: viewport_southwest.latitude,
                    },
                };

                res({
                    currentPlace: suggestedLocation.currentPlace,
                    currentPlaceParent: suggestedLocation.currentPlaceParent,
                    latitude,
                    longitude,
                    isOnline: false,
                    bbox,
                });

                trackMissingWOFPlace(
                    `${suggestedLocation.currentPlace}, ${suggestedLocation.currentPlaceParent}`,
                );
            }
        });
    });
};

export default class LocationAutocompleteGoogle extends PureComponent {
    static propTypes = {
        currentPlace: PropTypes.string,
        placeId: PropTypes.string,
        placeholder: PropTypes.string,
        onComponentDidMount: PropTypes.func,
        onComponentWillUnmount: PropTypes.func,
        onGoogleMapsLoaded: PropTypes.func,
        handleUseCurrentLocation: PropTypes.func,
        handleLocationSuggestionChange: PropTypes.func,
        // eslint-disable-next-line react/forbid-prop-types
        locationSuggestions: PropTypes.object,
        handleLocationSelect: PropTypes.func,
        isBBoxSearch: PropTypes.bool,
        isUseCurrentLocationEnabled: PropTypes.bool,
        isOnline: PropTypes.bool,
        isWaitingForLocation: PropTypes.bool,
        isUsingCurrentLocation: PropTypes.bool,
        disabled: PropTypes.bool,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        autocompleteProps: PropTypes.shape({
            borderType: PropTypes.string,
            id: PropTypes.string,
            name: PropTypes.string,
            hideOutline: PropTypes.bool,
            isLimitedSearch: PropTypes.bool,
            preventInputValuePrefill: PropTypes.bool,
        }),
        googleMapsApiKey: PropTypes.string,
    };

    static defaultProps = {
        autocompleteProps: {
            borderType: 'none',
            id: 'locationPicker',
            name: 'locationPicker',
            hideOutline: true,
            isLimitedSearch: true,
            preventInputValuePrefill: true,
        },
    };

    constructor(props) {
        super(props);

        const {
            currentPlace,
            isBBoxSearch,
            locationSuggestions: { suggestions },
            googleMapsApiKey,
        } = props;

        let locationQuery = currentPlace;

        if (isBBoxSearch) {
            locationQuery = BBOX_SEARCH_DISPLAY_STRING;
        }

        this.state = {
            locationQuery,
            suggestions,
            isTyping: false,
            selectedLocation: currentPlace,
            googleSuggestions: [],
        };

        this.historySuggestions = parseHistorySuggestions({
            storageKey: LOCATION_STORAGE_KEY,
        });

        if (!isGoogleMapsLoaded() && googleMapsApiKey) {
            const loadjs = require('loadjs');

            // Asynchronously load the Google Maps script, passing in the callback reference
            loadjs(
                `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`,
                () => {
                    if (this.props.onGoogleMapsLoaded) {
                        this.props.onGoogleMapsLoaded();
                    }
                },
            );
        }
    }

    componentDidMount() {
        const { onComponentDidMount } = this.props;

        if (onComponentDidMount) {
            onComponentDidMount();
        }

        this._isItemSelected = false;
        this._suggestionsPending = false;
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps({ currentPlace, isBBoxSearch }) {
        let newLocationQuery = currentPlace;

        if (isBBoxSearch) {
            newLocationQuery = BBOX_SEARCH_DISPLAY_STRING;
        }

        this.setState(
            ({
                isTyping,
                locationQuery: currentLocationQuery,
                selectedLocation: currentSelectedLocation,
            }) => {
                let locationQuery = currentLocationQuery;

                let selectedLocation = currentSelectedLocation;

                if (!isTyping) {
                    locationQuery = newLocationQuery;
                    selectedLocation = newLocationQuery;
                }

                return { locationQuery, selectedLocation };
            },
        );
    }

    componentWillUnmount() {
        const { onComponentWillUnmount } = this.props;

        if (onComponentWillUnmount) {
            onComponentWillUnmount();
        }
    }

    _handleLocationAutocompleteSelect = (selectedValue) => {
        const {
            handleLocationSelect,
            handleUseCurrentLocation,
            isUseCurrentLocationEnabled,
        } = this.props;

        // when an enter key is pressed, we want to make sure that any pending location queries
        // are completed.  i.e. If I type in "san" and the top suggestion is "san jose".  And
        // then I continue with "diego" and press enter quickly, the autocomplete component is
        // going to call this method before the suggestions for "san diego" have returned.
        if (this._suggestionsPending) {
            return;
        }

        this.setState({ locationQuery: '' });

        const { googleSuggestions } = this.state;
        const suggestedLocation = find(
            googleSuggestions,
            ({ value }) => value === selectedValue,
        );

        this._isItemSelected = true;

        if (selectedValue.startsWith('recent-')) {
            // Selected a location from history
            const historySuggestion = this.historySuggestions.find(
                ({ value }) => value === selectedValue,
            );

            handleLocationSelect(historySuggestion);

            this._isItemSelected = false;
        } else if (suggestedLocation && suggestedLocation.value) {
            getPlaceObj(suggestedLocation).then((placeObj) => {
                this.setState(
                    {
                        locationQuery: suggestedLocation.currentPlace,
                        selectedLocation: suggestedLocation.currentPlace,
                    },
                    () => {
                        handleLocationSelect(placeObj);

                        this._isItemSelected = false;
                    },
                );
            });
        } else if (selectedValue === 'online') {
            handleLocationSelect({
                ...getOnlineEventsOption(),
                placeId: null,
            });
            this._isItemSelected = false;
        } else if (isUseCurrentLocationEnabled) {
            this.setState(
                {
                    locationQuery: '',
                    selectedLocation: '',
                },
                () => {
                    handleUseCurrentLocation();
                    this._isItemSelected = false;
                },
            );
        }
    };

    _handleLocationAutocompleteChange = (locationQuery) => {
        this.setState({ locationQuery });
        this._suggestionsPending = true;
        this._requestLocationSuggestions(locationQuery);
    };

    _handleEnterKey = (query) => {
        if (this._suggestionsPending) {
            this._requestLocationSuggestions.cancel();
            this._handleGetSuggestionsFromGoogle(query, true);
        }
    };

    _handleLocationAutocompleteBlur = () => {
        const { selectedLocation } = this.state;

        this.setState({
            locationQuery: selectedLocation,
            isTyping: false,
        });
    };

    _handleLocationAutocompleteFocus = () => {
        /* The autocomplete will refocus on the input, triggering the handlefocus
        callback when an item is selected. We don't want to clear the input _after_
        a user selected an item but only when initially clicking. At the moment,
        selecting an item triggers a redirect so it will be reset each time. */
        if (!this._isItemSelected) {
            this.historySuggestions = parseHistorySuggestions({
                storageKey: LOCATION_STORAGE_KEY,
            });

            this.setState({
                locationQuery: '',
                isTyping: true,
                googleSuggestions: [],
            });

            this._requestLocationSuggestions('');
        }
    };

    /**
     * Make a request to google for location suggestions and process the results.
     *
     * @param {string} query The location query that should be completed e.g. "Los Ang"
     * @param {bool} selectFirstResult Select the first autocomplete result returned from
     *     google. Useful if a user presses the enter key quickly after typing.
     */
    _handleGetSuggestionsFromGoogle = (query, selectFirstResult) => {
        const { latitude, longitude } = this.props;

        if (!query || !isGoogleMapsLoaded()) {
            return this.setState({ googleSuggestions: [] });
        }

        let request = {
            input: query,
            types: ['(regions)'],
        };

        if (latitude && longitude) {
            request = {
                ...request,
                location: new window.google.maps.LatLng(latitude, longitude),
                // radius is in meters
                radius: 200000,
            };
        }

        const autocomplete = new window.google.maps.places.AutocompleteService();

        return autocomplete.getPlacePredictions(request, (places, status) => {
            this._suggestionsPending = false;

            // In the case of ZERO_RESULTS, the suggestions will be empty,
            // exit early so that we will keep the most recent set of results
            if (status === 'ZERO_RESULTS' && !places) {
                return;
            }

            if (status !== 'OK') {
                throw new Error(`Received error from Google places: ${status}`);
            }

            const placeSuggestions = places.map((place) => ({
                content: place.structured_formatting.main_text,
                secondaryContent: place.structured_formatting.secondary_text,
                value: place.place_id,
                currentPlace: place.structured_formatting.main_text,
                currentPlaceParent: place.structured_formatting.secondary_text,
            }));

            this.setState({ googleSuggestions: placeSuggestions });

            if (selectFirstResult) {
                this._handleLocationAutocompleteSelect(
                    placeSuggestions[0].value,
                );
            }
        });
    };

    _requestLocationSuggestions = debounce(
        this._handleGetSuggestionsFromGoogle,
        LOCATION_DEBOUNCE_INTERVAL,
    );

    render() {
        const {
            isWaitingForLocation,
            autocompleteProps,
            placeholder,
            isUseCurrentLocationEnabled,
            disabled,
        } = this.props;

        const {
            locationQuery,
            selectedLocation,
            googleSuggestions,
        } = this.state;

        // Note: Don't store in variable or else it can't be translated
        let value =
            locationQuery === gettext('Your Location')
                ? gettext('Your Location')
                : locationQuery;

        let _placeholder = placeholder || selectedLocation;

        if (_placeholder === gettext('Your Location')) {
            _placeholder = gettext('Your Location');
        }

        let suggestions = [];

        if (isGoogleMapsLoaded()) {
            if (!isEmpty(googleSuggestions)) {
                suggestions = [...googleSuggestions];
            } else {
                if (isUseCurrentLocationEnabled) {
                    suggestions = [
                        {
                            id: 'use-current',
                            content: gettext('Use my current location'),
                            value: '',
                            iconType: <CrosshairSvg />,
                        },
                    ];
                }
                suggestions = [
                    ...suggestions,
                    getOnlineEventsOption(),
                    ...this.historySuggestions,
                ];
            }
        }

        if (isWaitingForLocation) {
            value = '';
            _placeholder = gettext('Finding your location...');
        }

        return (
            <div
                className="location-autocomplete"
                data-spec="location-autocomplete"
            >
                <AutocompleteField
                    suggestions={suggestions}
                    value={value}
                    onBlur={this._handleLocationAutocompleteBlur}
                    onChange={this._handleLocationAutocompleteChange}
                    onFocus={this._handleLocationAutocompleteFocus}
                    onSelect={this._handleLocationAutocompleteSelect}
                    onEnterKey={this._handleEnterKey}
                    shouldFocusInputOnSelect={false}
                    isV2={true}
                    placeholder={_placeholder}
                    disabled={disabled || isWaitingForLocation}
                    {...autocompleteProps}
                />
            </div>
        );
    }
}
